"use client";

import { Progress as ProgressPrimitive } from "@base-ui/react/progress";

import { cn } from "@/lib/utils";

interface ProgressSegment {
  key: string;
  /** This segment's share of the track, 0-100. */
  value: number;
  /** Background utility for this segment; defaults to the indicator's. */
  className?: string;
}

/**
 * Pass `segments` for a stacked bar — several coloured runs across one track,
 * for a whole split by category rather than a single completed fraction. `value`
 * still carries the aggregate for assistive tech.
 */
function Progress({
  className,
  children,
  value,
  segments,
  ...props
}: ProgressPrimitive.Root.Props & { segments?: readonly ProgressSegment[] }) {
  return (
    <ProgressPrimitive.Root
      value={value}
      data-slot="progress"
      className={cn("flex flex-wrap gap-3", className)}
      {...props}
    >
      {children}
      <ProgressTrack className={segments && "items-stretch"}>
        {segments ? (
          segments.map((segment) => (
            <span
              key={segment.key}
              data-slot="progress-segment"
              className={cn("bg-primary", segment.className)}
              style={{ width: `${segment.value}%` }}
            />
          ))
        ) : (
          <ProgressIndicator />
        )}
      </ProgressTrack>
    </ProgressPrimitive.Root>
  );
}

function ProgressTrack({ className, ...props }: ProgressPrimitive.Track.Props) {
  return (
    <ProgressPrimitive.Track
      className={cn("bg-muted h-1.5 rounded-full relative flex w-full items-center overflow-x-hidden", className)}
      data-slot="progress-track"
      {...props}
    />
  );
}

function ProgressIndicator({ className, ...props }: ProgressPrimitive.Indicator.Props) {
  return (
    <ProgressPrimitive.Indicator
      data-slot="progress-indicator"
      className={cn("bg-primary h-full transition-all", className)}
      {...props}
    />
  );
}

function ProgressLabel({ className, ...props }: ProgressPrimitive.Label.Props) {
  return (
    <ProgressPrimitive.Label className={cn("text-sm font-medium", className)} data-slot="progress-label" {...props} />
  );
}

function ProgressValue({ className, ...props }: ProgressPrimitive.Value.Props) {
  return (
    <ProgressPrimitive.Value
      className={cn("text-muted-foreground ml-auto text-sm tabular-nums", className)}
      data-slot="progress-value"
      {...props}
    />
  );
}

export { Progress, ProgressTrack, ProgressIndicator, ProgressLabel, ProgressValue };
export type { ProgressSegment };
